import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectItem } from '@/components/ui/select';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  DollarSign,
  Loader2,
  Plus,
  Mail,
  Shield,
  Save,
  UserPlus,
} from 'lucide-react';
import type { ApiResponse, User, UserRole, InvitationInput } from '@/types';

export function SettingsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const { user, organization, refreshAuth } = useAuth();
  const [activeTab, setActiveTab] = useState('org');

  // Org form state
  const [orgName, setOrgName] = useState(organization?.name ?? '');
  const [abn, setAbn] = useState(organization?.abn ?? '');
  const [licenseNumber, setLicenseNumber] = useState(organization?.licenseNumber ?? '');
  const [address, setAddress] = useState(organization?.address ?? '');
  const [phone, setPhone] = useState(organization?.phone ?? '');
  const [isSavingOrg, setIsSavingOrg] = useState(false);

  // Team state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('staff');
  const [isSendingInvite, setIsSendingInvite] = useState(false);

  useEffect(() => {
    if (organization) {
      setOrgName(organization.name);
      setAbn(organization.abn ?? '');
      setLicenseNumber(organization.licenseNumber ?? '');
      setAddress(organization.address ?? '');
      setPhone(organization.phone ?? '');
    }
  }, [organization]);

  const handleSaveOrg = async () => {
    setIsSavingOrg(true);
    try {
      await api.patch('/auth/organization', {
        name: orgName,
        abn: abn || undefined,
        licenseNumber: licenseNumber || undefined,
        address: address || undefined,
        phone: phone || undefined,
      });
      await refreshAuth();
      toast.success('Organization updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update organization');
    } finally {
      setIsSavingOrg(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    setIsSendingInvite(true);
    try {
      await api.post('/auth/invitations', {
        email: inviteEmail,
        role: inviteRole,
      });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('staff');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setIsSendingInvite(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your organization, team, and billing</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="org">
            <Building2 className="mr-2 h-4 w-4" /> Organization
          </TabsTrigger>
          <TabsTrigger value="team">
            <Users className="mr-2 h-4 w-4" /> Team
          </TabsTrigger>
          <TabsTrigger value="rates">
            <DollarSign className="mr-2 h-4 w-4" /> Rate Catalog
          </TabsTrigger>
        </TabsList>

        {/* Organization Tab */}
        <TabsContent value="org" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Organization Details</CardTitle>
              <CardDescription>Your construction company information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="orgName">Organization Name</Label>
                <Input
                  id="orgName"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="abn">ABN</Label>
                  <Input
                    id="abn"
                    value={abn}
                    onChange={(e) => setAbn(e.target.value)}
                    placeholder="12 345 678 901"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="license">License Number</Label>
                  <Input
                    id="license"
                    value={licenseNumber}
                    onChange={(e) => setLicenseNumber(e.target.value)}
                    placeholder="Builder License #"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="123 Builder St, Sydney NSW 2000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(02) 9123 4567"
                />
              </div>
              <Button onClick={handleSaveOrg} disabled={isSavingOrg}>
                {isSavingOrg ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Team Members</h2>
              <p className="text-sm text-muted-foreground">Manage who has access to your organization</p>
            </div>
            <Button onClick={() => setShowInvite(true)}>
              <UserPlus className="mr-2 h-4 w-4" /> Invite Member
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {user && (
                    <TableRow>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={user.role === 'admin' ? 'default' : 'outline'}>
                          <Shield className="mr-1 h-3 w-3" />
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(user.createdAt).toLocaleDateString('en-AU')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Invite Dialog */}
          <Dialog open={showInvite} onOpenChange={setShowInvite}>
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
              <DialogDescription>
                Send an invitation email to join your organization
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="inviteEmail">Email *</Label>
                <Input
                  id="inviteEmail"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inviteRole">Role</Label>
                <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as UserRole)}>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="trade">Trade</SelectItem>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInvite(false)}>
                Cancel
              </Button>
              <Button onClick={handleInvite} disabled={isSendingInvite}>
                {isSendingInvite ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                Send Invitation
              </Button>
            </DialogFooter>
          </Dialog>
        </TabsContent>

        {/* Rate Catalog Tab */}
        <TabsContent value="rates">
          <Card>
            <CardHeader>
              <CardTitle>Rate Catalog</CardTitle>
              <CardDescription>
                Standard rates for labor, materials, and services used in estimates
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Rate ($)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>Carpentry</TableCell>
                      <TableCell>Rough carpentry — framing</TableCell>
                      <TableCell>hr</TableCell>
                      <TableCell className="text-right">$85.00</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Carpentry</TableCell>
                      <TableCell>Finish carpentry — trim/doors</TableCell>
                      <TableCell>hr</TableCell>
                      <TableCell className="text-right">$95.00</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Plumbing</TableCell>
                      <TableCell>Rough-in plumbing</TableCell>
                      <TableCell>hr</TableCell>
                      <TableCell className="text-right">$120.00</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Electrical</TableCell>
                      <TableCell>Rough-in electrical</TableCell>
                      <TableCell>hr</TableCell>
                      <TableCell className="text-right">$110.00</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Painting</TableCell>
                      <TableCell>Interior painting — walls/ceilings</TableCell>
                      <TableCell>m²</TableCell>
                      <TableCell className="text-right">$35.00</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              <Button variant="outline" className="mt-4">
                <Plus className="mr-2 h-4 w-4" /> Add Rate
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
